import {useMutation} from '@apollo/client'
import FacebookLogin from '@greatsumini/react-facebook-login'
import {Box, Button, Grid2, Link, Typography} from '@mui/material'
import gql from 'graphql-tag'
import {useCallback} from 'react'
import {useNotify, useRefresh, useTranslate} from 'react-admin'
import {makeStyles} from 'tss-react/mui'

import FileUploadButton from '../../components/FileUploadButton'
import {WhatsappIcon} from '../../components/icons'
import {MerchantUserCreate} from '../../resources/merchantUsersResource'
import {MutationRoot} from '../../types/graphqlSchema'
import env from '../../utils/env'
import {isNativeMobileApp} from '../../utils/platform'

const AddNumbersAndMerchantUsers = () => {
  const {classes: styles} = useStyles({})
  return (
    <Grid2>
      <div>
        <Link
          href={ONBOARDING_RESOURCE_URL}
          {...(!isNativeMobileApp && {rel: 'noreferer', target: '_blank'})}
          underline="hover">
          {useTranslate()(
            'pages.companySettings.tabs.addNumbersAndMerchantUsers.faq.linkText'
          )}
        </Link>
        {' '}{useTranslate()(
          'pages.companySettings.tabs.addNumbersAndMerchantUsers.faq.text'
        )}
      </div>
      <Box className={styles.addWhatsappAccountContainer}>
        <div>
          <Typography variant="subtitle1" style={{marginBottom: '0.5rem'}}>
            {useTranslate()(
              'pages.companySettings.tabs.addNumbersAndMerchantUsers.createNumber.title'
            )}
          </Typography>
          <Typography>
            {useTranslate()(
              'pages.companySettings.tabs.addNumbersAndMerchantUsers.createNumber.' +
              'subtitle'
            )}
          </Typography>
        </div>
        <VonageConnectionButton />
      </Box>
      <Box>
        <Box display="flex" justifyContent="space-between">
          <div>
            <Typography variant="subtitle1" style={{marginBottom: '0.5rem'}}>
              {useTranslate()(
                'pages.companySettings.tabs.addNumbersAndMerchantUsers.' +
                'createMerchantUser.title'
              )}
            </Typography>
            <Typography>
              {useTranslate()(
                'pages.companySettings.tabs.addNumbersAndMerchantUsers.' +
                'createMerchantUser.subtitle'
              )}
            </Typography>
          </div>
          <MerchantUsersImportButton />
        </Box>
        <MerchantUserCreate />
      </Box>
    </Grid2>
  )
}

const MerchantUsersImportButton = () => {
  const notify = useNotify()
  const refresh = useRefresh()
  const translate = useTranslate()
  const [
    importMerchantUsers,
    {loading: isImportingContactFilters},
  ] = useMutation<MutationRoot['importMerchantUsers']>(
    MERCHANT_USERS_IMPORT_MUTATION,
    {
      onCompleted: ({importMerchantUsers}) => {
        refresh()
        notify(
          'resources.merchant_users.import.success',
          {
            messageArgs: {importMerchantUsersCount: importMerchantUsers.length},
            type: 'success',
          },
        )
      },
      onError: e => {
        notify(translate(e.message), {type: 'error'})
      },
    }
  )
  const importMerchantUsersCsv = useCallback(([file]: FileAttachment[]) => {
    if (window.confirm(
      translate('resources.merchant_users.import.confirm', {filename: file.filename})
    )) {
      importMerchantUsers({variables: {csvBase64Content: file.encodedContent}})
    }
  }, [importMerchantUsers, translate])
  return (
    <FileUploadButton
      loading={isImportingContactFilters}
      mimeTypes={['text/csv']}
      onSelectFiles={importMerchantUsersCsv}
    >
      {translate('resources.merchant_users.import.button')}
    </FileUploadButton>
  )
}

const VonageConnectionButton = () => {
  const notify = useNotify()
  const translate = useTranslate()
  const [connectVonageChannels, {loading}] = useMutation(
    CONNECT_VONAGE_CHANNELS_MUTATION,
  )
  return (
    <FacebookLogin
      appId={FACEBOOK_APP_CLIENT_ID}
      initParams={{
        cookie: true,
        // @ts-expect-error
        // The package's Graph API version union is outdated and does not include v26.0.
        version: FACEBOOK_API_VERSION,
        xfbml: true,
      }}
      loginOptions={{
        config_id: FACEBOOK_APP_CONFIG_ID,
        // @ts-ignore
        extras: {setup: {solutionID: VONAGE_PARTNER_SOLUTION_ID}},
        override_default_response_type: true,
        response_type: 'code',
        return_scopes: true,
      }}
      onFail={err => {
        if (err.status === "facebookNotLoaded" || err.status === 'loginCancelled') {
          return
        }
        console.error(err)
        notify('pages.whatsappChannelsConnection.connectionError', {type: 'error'})
      }}
      onSuccess={
        // @ts-ignore
        res => connectVonageChannels({variables: {code: res.code}})
          .then(() => notify(
            'pages.whatsappChannelsConnection.connectionSuccess',
            {type: 'success'},
          ))
          .catch(err => {
            console.error(err)
            notify('pages.whatsappChannelsConnection.connectionError', {type: 'error'})
          })
      }
      render={({onClick}) => (
        <Button
          disabled={loading}
          onClick={onClick}
          startIcon={<WhatsappIcon />}
          variant="outlined"
        >
          {translate(
            'pages.companySettings.tabs.addNumbersAndMerchantUsers.createNumber.' +
            'whatsappConnectionButton',
          )}
        </Button>
      )}
    />
  )
}

const CONNECT_VONAGE_CHANNELS_MUTATION = gql`
  mutation($code: String!){
    connectVonageChannels(code: $code){whatsappAccount{id phoneNumber}}
  }
`

const MERCHANT_USERS_IMPORT_MUTATION = gql`
  mutation($csvBase64Content: String!){
    importMerchantUsers(csvBase64Content: $csvBase64Content){
      merchantUser{id}
    }
  }
`

const useStyles = makeStyles<any>()(() => ({
  addWhatsappAccountContainer: {
    borderBottom: '1px solid #ccc',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '3rem',
    paddingBottom: '3rem',
    paddingTop: '2rem',
  },
}))

const FACEBOOK_APP_CLIENT_ID = env('FACEBOOK_APP_CLIENT_ID')
const FACEBOOK_APP_CONFIG_ID = env('FACEBOOK_APP_CONFIG_ID')
const FACEBOOK_API_VERSION = 'v26.0'
const VONAGE_PARTNER_SOLUTION_ID = env('VONAGE_PARTNER_SOLUTION_ID')
const ONBOARDING_RESOURCE_URL = 'https://flinkit-customer-support-center.netlify.app/'

export default AddNumbersAndMerchantUsers
